import React, { useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  TextField,
  Grid,
  Typography,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import Iconify from '../common/iconify.component';
import SnackbarInfo from '../common/snackbar-info.component';
import { createNewData } from '../../services/videos.service';
import StyledDialogActions from '../../theme/dialogActionStyles';
import VideoTypes from '../form-elements/video-types.component';
import VideoSources from '../form-elements/video-sources.component';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const CreateContentVideo = ({ title, onCancel, onSuccess }) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {
      title: '',
      type: '',
      videoSource: 'FILE',
      status: 1,
    },
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const watchedSource = watch('videoSource');

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      title: data.title,
      type: data.type,
      videoSource: data.videoSource,
      youtubeVideoId: data.youtubeVideoId,
      status: 1,
    };
    createNewData(payload)
      .then(() => {
        onSuccess('New video created successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="add-dialog-title">{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="add-dialog-description">
        <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12}>
              <Controller
                name="title"
                control={control}
                rules={{
                  ...validationRules.REQUIRED,
                  ...maxLengthValidation(128),
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Title *"
                    type="text"
                    error={!!errors.title}
                    helperText={errors?.title?.message || null}
                    fullWidth
                    variant="filled"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12}>
              <Controller
                name="type"
                control={control}
                rules={validationRules.REQUIRED}
                render={({ field: { onChange, value } }) => (
                  <VideoTypes
                    id="video_type"
                    name="video_type"
                    label="Type *"
                    defaultValue={value}
                    onChange={onChange}
                    error={errors?.type?.message || ''}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12}>
              <Controller
                name="videoSource"
                control={control}
                rules={validationRules.REQUIRED}
                render={({ field: { onChange, value } }) => (
                  <VideoSources
                    id="video_source"
                    name="video_source"
                    label="Video Source *"
                    defaultValue={value}
                    onChange={onChange}
                    error={errors?.Controller?.message || ''}
                  />
                )}
              />
            </Grid>

            {watchedSource === 'YOUTUBE' && (
              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="youtubeVideoId"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...maxLengthValidation(11),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="YouTube Video Id *"
                      type="text"
                      error={!!errors.youtubeVideoId}
                      helperText={errors?.youtubeVideoId?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </form>
      </DialogContent>

      <StyledDialogActions>
        <Grid>
          <Button
            type="submit"
            form="add-edit-form"
            color="primary"
            variant="contained"
            disabled={formSubmitted}
            sx={{ marginRight: 2 }}
          >
            Save
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={onCancel}
            disabled={formSubmitted}
          >
            Cancel
          </Button>
        </Grid>
        <Grid>
          <Typography variant="body2" color="secondary">
            * indicates a required field
          </Typography>
        </Grid>
      </StyledDialogActions>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

CreateContentVideo.propTypes = {
  title: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default CreateContentVideo;
